import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { groupBy } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { Papa } from 'ngx-papaparse';
import { CsvError } from 'src/app/core/model/error';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { CommunityProviderProgramService } from 'src/app/core/service/community-provider-program.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';

@Component({
  selector: 'app-community-provider-program-import',
  templateUrl: './community-provider-program-import.component.html',
  styleUrls: ['./community-provider-program-import.component.scss']
})
export class CommunityProviderProgramImportComponent implements OnInit
{

  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Output() public loadedCommunityPrograms: EventEmitter<any> = new EventEmitter();

  public enableAdvanced = false;
  public view: GridViewManager;
  public dialogOpened = false;
  public communityProviderProgramsImported = false;
  public importForm: any;
  public resultcolumns: any[] = [
    { field: 'id', titleKey: 'ID', width: 20 },
    { field: 'name', titleKey: 'NAME', width: 60 },
    { field: 'message', titleKey: 'MESSAGE', width: 80 },
    { field: 'valid', titleKey: 'IMPORT-PROVIDERS-VALID', width: 20 }
  ];
  public importResults = [];
  public csvFile: File;
  public totalErrors = 0;
  public totalSuccesses = 0;
  public textYES = '';
  public textNO = '';
  public communityProviderProgramResultTexts: Array<{ text: string; key: string }> = [];
  public selectedTab = 0;

  /**
   * Errors when parsing the CSV file.
   */
  public csvErrors: CsvError[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private papa: Papa,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private communityProviderProgramService: CommunityProviderProgramService)
  { }

  public get importFilename(): UntypedFormControl
  {
    return this.importForm.get('importFilename');
  }

  public get validateOnly(): UntypedFormControl
  {
    return this.importForm.get('validateOnly');
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(99999);

    this.translate.get(['YES', 'NO']).subscribe({
      next: (res) =>
      {
        this.textYES = res.YES;
        this.textNO = res.NO;
      }
    });

    this.translate.get(['IMPORT-COMMUNITY-PROGRAMS-NOT-FOUND', 'IMPORT-COMMUNITY-PROGRAMS-TO-UPDATE',
      'IMPORT-COMMUNITY-PROGRAMS-TO-CREATE',
      'IMPORT-COMMUNITY-PROGRAMS-UPDATED',
      'IMPORT-COMMUNITY-PROGRAMS-CREATED',
      'INVALID-OPEN-TIME',
      'INVALID-STATE',
      'INVALID-COUNTRY',
      'IMPORT-COMMUNITY-PROGRAMS-OPENING-TIMES-INVALID',
      'IMPORT-COMMUNITY-PROGRAMS-FORBIDDEN',
      'IMPORT-COMMUNITY-PROGRAMS-ORGANIZATION-FORBIDDEN',
      'IMPORT-COMMUNITY-PROGRAMS-LANGUAGE-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-SERVICE-CATEGORY-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-ERROR',
      'IMPORT-COMMUNITY-PROGRAMS-URL-INVALID',
      'IMPORT-EMAIL-INVALID',
      'IMPORT-PHONE-INVALID',
      'IMPORT-EMERGENCY-NUMBER-INVALID',
      'IMPORT-FAX-INVALID',
      'IMPORT-COMMUNITY-PROGRAMS-LICENSE-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-PROVIDER-TAXONOMY-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-RESOURCE-STATUS-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-OTHER_IDENTIFIERS-INVALID',
      'IMPORT-COMMUNITY-PROGRAMS-WAIT-TIMES-INVALID',
      'IMPORT-COMMUNITY-PROGRAMS-WEBSITES-INVALID',
      'IMPORT-COMMUNITY-PROGRAMS-AREA-SERVED-CITY-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-AREA-SERVED-COUNTY-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-AREA-SERVED-ZIPCODE-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-INCOME-REQUIREMENT-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-PAYMENT-METHOD-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-TARGETED-DEMOGRAPHIC-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-TARGETED-POPULATION-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-ORGANIZATION-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-SERVICE-CATEGORY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-SERVICE-NAME-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-SUBMITTING-ORGANIZATION-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-SUBMITTING-ORGANIZATION-REQUIRED-NOT-SUBMITTING',
      'IMPORT-COMMUNITY-PROGRAMS-STATUS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ALIAS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-DESCRIPTION-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ELIGIBILITY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-COST-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-LANGUAGE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-AREA-SERVED-CITY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-AREA-SERVED-COUNTY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-AREA-SERVED-ZIPCODE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-STREET-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ADDRESS-LINE-2-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-CITY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-POSTAL-CODE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-STATE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-COUNTRY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PROGRAM-CONTACT-NAME-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PROGRAM-CONTACT-EMAIL-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PROGRAM-CONTACT-PHONE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PROGRAM-CONTACT-FAX-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PROGRAM-CONTACT-URL-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-NAME-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-EMAIL-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-PHONE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-FAX-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-EMERGENCY-NUMBER-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-EXTENSION-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-REFERRAL-CONTACT-URL-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-APPLICATION-PROCESS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ALERT-DESCRIPTION-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-DOCUMENTATION-REQUIRED-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ACCEPTING-DISABLED-PATIENTS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ACCEPTING-ELECTRONIC-REFERRALS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-LGBTQ-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ACCEPTING-NEW-PATIENTS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ACCEPTING-SELF-REFERRALS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-CAPACITY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PAYMENT-METHOD-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-INCOME-REQUIREMENT-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-PROVIDER-TAXONOMY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-LICENSE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-TARGETED-DEMOGRAPHIC-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-TARGETED-POPULATION-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-DIRECTORY-MANAGER-NAME-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-DIRECTORY-MANAGER-EMAIL-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ABLE-TO-CONDUCT-SCREENINGS-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-DIRECTORY-MANAGER-NOTIFICATION-PREFERENCE-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-DIRECTORY-MANAGER-NOTIFICATION-PREFERENCE-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-INFORMATIONAL-ONLY-REQUIRED',
      'IMPORT-COMMUNITY-PROGRAMS-ENHANCED-SERVICE-NOT-FOUND',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-NAME',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-DESCRIPTION',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-COST',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-ELIGIBILITY',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-ADDRESS-LINE-1',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-ADDRESS-LINE-2',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-CITY',
      'IMPORT-COMMUNITY-PROGRAMS-NON-ASCII-CHARACTERS-FOUND-SERVICE-POSTAL-CODE',
      'IMPORT-COMMUNITY-PROGRAMS-MULTIPLE-SERVICES-FOUND-FOR-A-SINGLE-IDENTIFIER',
      'IMPORT-COMMUNITY-PROGRAMS-MULTIPLE-SERVICES-FOUND-FOR-DIFFERENT-IDENTIFIERS',
      'IMPORT-COMMUNITY-PROGRAMS-ALLOW-DUPLICATE-REFERRALS-REQUIRED'
    ]).subscribe({
      next: (res) =>
      {
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            const resultText: any = {};
            resultText.text = res[key];
            resultText.key = key;
            this.communityProviderProgramResultTexts.push(resultText);
          }
        }
      }
    });
    this.resetGridData();
  }

  /**
   * Execute method to open the export dialog.
   */
  public execute(): void
  {
    this.buildForm();
    this.resetGridData();
    this.dialogOpened = true;
  }

  public close(): void
  {
    this.dialogOpened = false;
    if (this.communityProviderProgramsImported)
    {
      this.loadedCommunityPrograms.emit();
    }
  }

  /**
   * Importing Providers.
   *
   * @param e Event
   */
  public onImport(): void
  {
    // Import
    this.importCommunityProviderPrograms();
  }

  /**
   * Process the code CSV file import.
   */
  public handleCsvInput(files: FileList): void
  {
    this.csvFile = files.item(0);

    this.importForm.importFilename = this.csvFile.name;
    this.importFilename.setValue(this.csvFile.name);
    this.importForm.markAsDirty();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  public onTabSelect(e: SelectEvent): void
  {
    this.selectedTab = e.index;
  }

  /**
   * Create import form.
   */
  private buildForm(): void
  {
    // Initialization
    this.communityProviderProgramsImported = false;

    // Set form
    this.importForm = this.fb.group({
      importFilename: [
        { value: null, disabled: true },
        {
          validators: [Validators.required]
        }
      ],
      validateOnly: [{ value: true, disabled: false }]
    });
  }

  private resetGridData(): void
  {
    this.importResults = [];
    this.totalErrors = 0;
    this.totalSuccesses = 0;
    this.view.setData(this.importResults);
  }

  private importCommunityProviderPrograms(): void
  {
    // Reset import results
    this.resetGridData();

    this.view.startLoading();
    this.tabstrip.selectTab(1);

    // Parse csv
    this.papa.parse(this.csvFile,
      {
        header: true,           // CSV file must have a header row
        delimiter: ',',         // values must be delimited by commas
        skipEmptyLines: true,   // Skip empty lines
        complete: result =>     // parsing file is async, and here is the call back function.
        {
          // create new codes
          const programs = result.data.map(
            x =>
            ({
              id: x.id,
              providerName: x.providerName,
              organizationType: x.organizationType,
              serviceName: x.serviceName,
              cost: x.cost,
              eligibility: x.eligibility,
              openingTimes: x.openingTimes,
              category: x.category,
              languages: x.languages,
              description: x.description,
              addressLine1: x.addressLine1,
              addressLine2: x.addressLine2,
              city: x.city,
              state: x.state,
              postalCode: x.postalCode,
              country: x.country,
              longitude: x.longitude,
              latitude: x.latitude,
              programContactName: x.programContactName,
              programPhoneNumber: x.programPhoneNumber,
              programFaxNumber: x.programFaxNumber,
              programEmail: x.programEmail,
              programUrl: x.programUrl,
              referralContactName: x.referralContactName,
              referralPhoneNumber: x.referralPhoneNumber,
              referralFaxNumber: x.referralFaxNumber,
              referralEmail: x.referralEmail,
              referralUrl: x.referralUrl,
              referralEmergencyNumber: x.referralEmergencyNumber,
              referralExtension: x.referralExtension,
              alias: x.alias,
              alertDescription: x.alertDescription,
              applicationProcess: x.applicationProcess,
              capacity: x.capacity,
              documentationRequired: x.documentationRequired,
              acceptingDisabledPatients: x.acceptingDisabledPatients,
              acceptingElectronicReferrals: x.acceptingElectronicReferrals,
              acceptingNewPatients: x.acceptingNewPatients,
              acceptingSelfReferrals: x.acceptingSelfReferrals,
              lgbtq: x.lgbtq,
              submittingOrganization: x.submittingOrganization,
              resourceStatus: x.resourceStatus,
              areaServedCities: x.areaServedCities,
              areaServedCounties: x.areaServedCounties,
              areaServedZipCodes: x.areaServedZipCodes,
              incomeRequirements: x.incomeRequirements,
              paymentMethods: x.paymentMethods,
              licenses: x.licenses,
              targetedDemographics: x.targetedDemographics,
              targetedPopulations: x.targetedPopulations,
              providerTaxonomies: x.providerTaxonomies,
              otherIdentifiers: x.otherIdentifiers,
              waitTimes: x.waitTimes,
              websites: x.websites,
              directoryManagerName: x.directoryManagerName,
              directoryManagerEmail: x.directoryManagerEmail,
              directoryManagerNotificationPreference: x.directoryManagerNotificationPreference,
              enhancedServices: x.enhancedServices,
              ableToConductScreenings: x.ableToConductScreenings,
              informationalOnly: x.informationalOnly,
              externalID: x.externalID,
              allowDuplicateReferrals: x.allowDuplicateReferrals
            }));

          this.csvErrors = result.errors;

          if (result.errors.length === 0) // no errors in the CSV file
          {
            this.communityProviderProgramService.addUpdateCommunityPrograms(programs,
              this.validateOnly.value).subscribe({
                next: (data) =>
                {
                  data.forEach(d =>
                  {
                    d.valid = d.valid ? this.textYES : this.textNO;
                    d.message = '';
                    d.clientMessageKeys.forEach(m =>
                    {
                      const message = this.communityProviderProgramResultTexts.find(x => x.key === m);
                      if (message !== undefined)
                      {
                        d.message += d.message !== '' ? ` ${message.text}` : `${message.text}`;
                      }
                      else
                      {
                        d.message += d.message !== '' ? ` ${m}` : `${m}`;
                      }
                    });
                  });

                  // Set imported counts
                  const groups = groupBy(data, 'valid');
                  this.totalErrors = groups[this.textNO] !== undefined ? groups[this.textNO].length : 0;
                  this.totalSuccesses = groups[this.textYES] !== undefined ? groups[this.textYES].length : 0;

                  // Set gridData
                  this.importResults = data;
                  this.view.setData(this.importResults);
                  this.view.stopLoading();

                  // If providers have been loaded, this will trigger a reload from the grid list on close
                  if (!this.validateOnly.value)
                  {
                    this.communityProviderProgramsImported = true;
                  }

                  if (data && data.length < programs.length)
                  {
                    this.messageHandlerService.displayError('COMMUNITY-PROGRAMS-SAVE-ERROR');
                  }
                },
                error: () =>
                {
                  this.messageHandlerService.displayError('COMMUNITY-PROGRAMS-SAVE-ERROR');
                  this.view.stopLoading();
                }
              });
          }
          else
          {
            this.messageHandlerService.displayError('CSV-PARSING-ERRORS');
            this.logger.error(this.csvErrors);
            this.view.stopLoading();
          }
        },
        // IO error encountered when reading the CSV file
        error: (error) =>
        {
          this.logger.error(error);
          this.view.stopLoading();
        }
      });
  }
}
